import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { EmptyState } from '@/components/shared/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useToolSelector } from '../hooks/useToolSelector';
import type { LabelingExperiment } from '../types/LabelingTypes';
import { LabelExportButton } from './LabelExportButton';

// ---------- Props ----------

interface ToolSelectorProps {
  onStart: (toolId: string, sessionId: string) => void;
}

// ---------- Skeletons ----------

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          data-testid="skeleton-row"
          className="flex items-center justify-between px-3 py-2"
        >
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-4 w-6 rounded-full" />
        </div>
      ))}
    </>
  );
}

function SkeletonCards() {
  return (
    <Card data-testid="skeleton-card" className="flex flex-col gap-4">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-5 w-2/3 rounded-full" />
      <Skeleton className="h-8 w-full" />
    </Card>
  );
}

// ---------- Experiment summary ----------

interface ExperimentSummaryProps {
  experiment: LabelingExperiment;
  onStart: (toolId: string, sessionId: string) => void;
  startLabeling: (expId: string) => Promise<{ toolId: string; sessionId: string }>;
}

/**
 * One card per selected experiment: how many parts it holds, their ids as read-only
 * chips, and the single experiment-scoped "start labeling" action. Part ids are
 * informational — a session always covers the whole experiment.
 */
function ExperimentSummary({ experiment, onStart, startLabeling }: ExperimentSummaryProps) {
  const { t } = useTranslation();
  const [launching, setLaunching] = React.useState(false);

  const handleStart = async () => {
    setLaunching(true);
    try {
      const { toolId: tid, sessionId } = await startLabeling(experiment.experimentId);
      onStart(tid, sessionId);
    } finally {
      setLaunching(false);
    }
  };

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold tabular-nums text-foreground">
          {experiment.toolIds.length}
        </span>
        <span className="text-sm text-muted-foreground">{t('labeling.selector.partCount')}</span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {experiment.toolIds.map((id) => (
          <Badge key={id} data-testid={`part-chip-${id}`} className="tabular-nums">
            {id}
          </Badge>
        ))}
      </div>

      <Button className="w-full" loading={launching} onClick={() => void handleStart()}>
        {t('labeling.selector.startLabeling')}
      </Button>
    </Card>
  );
}

// ---------- Experiment row ----------

interface ExpRowProps {
  exp: LabelingExperiment;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

function ExpRow({ exp, isSelected, onSelect }: ExpRowProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(exp.experimentId)}
      className={cn(
        'flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer w-full text-left transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
        isSelected
          ? 'bg-primary/10 text-primary font-semibold'
          : 'text-foreground hover:bg-muted'
      )}
    >
      <span className="text-sm truncate">{exp.experimentId}</span>
      <Badge size="sm" className="ml-2 tabular-nums">
        {exp.toolIds.length}
      </Badge>
    </button>
  );
}

// ---------- Main component ----------

export function ToolSelector({ onStart }: ToolSelectorProps) {
  const { t } = useTranslation();
  const {
    experiments,
    selectedExpId,
    isLoading,
    error,
    setSelectedExpId,
    retry,
    startLabeling,
  } = useToolSelector();

  const selectedExp = experiments.find((e) => e.experimentId === selectedExpId) ?? null;

  // Error state
  if (error) {
    return (
      <div className="p-4">
        <EmptyState
          tone="error"
          title={t('labeling.selector.errorTitle')}
          action={
            <Button variant="outline" onClick={retry}>
              {t('labeling.selector.errorRetry')}
            </Button>
          }
        />
      </div>
    );
  }

  // Empty state (not loading, no experiments)
  if (!isLoading && experiments.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          title={t('labeling.selector.emptyTitle')}
          description={t('labeling.selector.emptyHint')}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Left panel — experiment list */}
      <div className="w-48 shrink-0 border-r border-border flex flex-col overflow-hidden">
        <div className="eyebrow px-3 py-2 border-b border-border">
          {t('labeling.selector.experiments')}
        </div>
        <div className="flex-1 overflow-y-auto py-1 px-1">
          {isLoading ? (
            <SkeletonRows />
          ) : (
            experiments.map((exp) => (
              <ExpRow
                key={exp.experimentId}
                exp={exp}
                isSelected={selectedExpId === exp.experimentId}
                onSelect={setSelectedExpId}
              />
            ))
          )}
        </div>
        <LabelExportButton experiments={experiments} disabled={isLoading} />
      </div>

      {/* Right panel — tools */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Right panel header */}
        <div className="eyebrow px-4 py-2 border-b border-border">
          {selectedExp
            ? `${t('labeling.selector.toolsIn')} ${selectedExp.experimentId}`
            : t('labeling.selector.toolsIn')}
        </div>

        {/* Right panel content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="max-w-md">
              <SkeletonCards />
            </div>
          ) : !selectedExp ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-sm text-muted-foreground">
                {t('labeling.selector.selectExperimentHint')}
              </span>
            </div>
          ) : (
            <div className="max-w-md">
              <ExperimentSummary
                experiment={selectedExp}
                onStart={onStart}
                startLabeling={startLabeling}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
